import * as React from 'react';
import cx from 'classnames';

export interface ITabItem {
  id?: string;
  title: string;
  component: any;
}

interface TabProps {
  /**
   * Item to be renderd in tabs
   * @type `ITabItem` `{title: string, component: any}`
   */
  items: ITabItem[];
  /**
   * Index of the item to be active on the initial render
   * @type number
   */
  initialTabIndex?: number;
  /**
   * classes to be added to the component container.
   * @type string
   */
  componentContainerClass?: string;
  /**
   * Custom classes to be added in the main container.
   * @type string
   */
  containerClass?: string;
  /**
   * Custom classes to be added to the tab container.
   * @type string
   */
  tabContainerClass?: string;
  /**
   * Function called with the active tab item.
   * @param item ITabItem
   * @return void
   */
  onChangeTab?: (item: ITabItem) => void;
}

const Tab: React.FC<TabProps> = ({
  items,
  initialTabIndex = 0,
  onChangeTab,
  containerClass,
  componentContainerClass,
  tabContainerClass,
}) => {
  const [activeTab, setActiveTab] = React.useState<number>(0);

  React.useEffect(() => {
    if (initialTabIndex <= items.length - 1) {
      setActiveTab(initialTabIndex);
    }
  }, []);

  const handleTabClick = (item: ITabItem, index: number) => {
    setActiveTab(index);
    onChangeTab && onChangeTab(item);
  };

  return (
    <div className={containerClass}>
      <ul className={'flex border w-[82%] md:min-w-md rounded-[5px] p-[3px] bg-[#fff0e6] mx-auto h-[48px] ' + tabContainerClass}>
        {items.map((item, index) => (
          <li
            key={`tab-${index}`}
            className={cx({
              'w-full': items.length === 1,
              'w-1/2 ': items.length > 1,
              'cursor-pointer h-full items-center flex justify-center': true,
              'font-medium rounded-[3.31px] bg-white': activeTab === index,
            })}
            onClick={() => handleTabClick(item, index)}
          >
            {item.title}
          </li>
        ))}
      </ul>
      <React.Suspense fallback="Loading...">
        <div className={componentContainerClass}>{items[activeTab].component}</div>
      </React.Suspense>
    </div>
  );
};

export { Tab };
